import React, { useEffect, useState } from "react";
import {
  Users,
  TrendingUp,
  TrendingDown,
  Minus,
  Sparkles,
  Timer,
  Trophy,
  Banknote,
  Building2,
  Filter,
  Loader2,
  ArrowRight,
  BarChart3,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "src/contexts/AuthContext";
import { cn } from "src/utils/cn";
import { AttendanceClockWidget } from "src/features/attendance/components/AttendanceClockWidget";
import { MyDayWidget } from "src/features/tasks/components/MyDayWidget";
import { ExpiringDocumentsWidget } from "src/features/documents/components/ExpiringDocumentsWidget";
import { fetchOverview, fetchInventory } from "src/features/analytics/api/analyticsApi";
import { formatINR, formatNumber, CHART_COLORS } from "src/features/analytics/constants";

/* ── Delta arrow ─────────────────────────────────────────────────────────── */
function Delta({ value, invertColor = false }) {
  if (value == null) return null;
  const positive = value > 0;
  const negative = value < 0;
  const color = invertColor
    ? positive ? "text-red-500" : negative ? "text-emerald-600" : "text-slate-400"
    : positive ? "text-emerald-600" : negative ? "text-red-500" : "text-slate-400";
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-[12px] font-medium", color)}>
      <Icon className="h-3.5 w-3.5" />
      {value === 0 ? "0%" : `${positive ? "+" : ""}${value}%`}
    </span>
  );
}

/* ── KPI card (dashboard version — compact) ──────────────────────────────── */
function KpiTile({ label, value, suffix, delta, invertDelta, sublabel, icon: Icon, accentColor }) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
      <div className="absolute inset-x-0 top-0 h-[3px] rounded-t-xl" style={{ backgroundColor: accentColor }} />
      <div className="flex items-start justify-between pt-0.5">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">{label}</p>
          <p className="mt-1.5 flex items-baseline gap-1">
            <span className="text-2xl font-bold tracking-tight text-slate-900">{value ?? "—"}</span>
            {suffix && <span className="text-[13px] text-slate-400">{suffix}</span>}
          </p>
          <div className="mt-1 flex items-center gap-2">
            <Delta value={delta} invertColor={invertDelta} />
            {sublabel && <span className="text-[11px] text-slate-400">{sublabel}</span>}
          </div>
        </div>
        {Icon && (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 ring-1 ring-slate-100">
            <Icon className="h-4 w-4 text-slate-400" />
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Mini funnel bar (inline sparkline) ──────────────────────────────────── */
function MiniFunnel({ stages = [] }) {
  if (!stages.length) return null;
  const max = Math.max(...stages.map((s) => s.count), 1);
  return (
    <div className="flex items-end gap-1.5 h-16">
      {stages.map((s, i) => {
        const pct = Math.max((s.count / max) * 100, 6);
        return (
          <div key={i} className="flex flex-1 flex-col items-center gap-0.5" title={`${s.name}: ${s.count}`}>
            <span className="text-[10px] font-semibold text-slate-600">{s.count}</span>
            <div
              className="w-full rounded-t transition-all"
              style={{ height: `${pct}%`, minHeight: 4, backgroundColor: CHART_COLORS.primary, opacity: 1 - i * 0.15 }}
            />
            <span className="w-full truncate text-center text-[9px] text-slate-400">{s.name}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Mini horizontal status bar (inventory) ──────────────────────────────── */
function InventoryBar({ totals }) {
  if (!totals || totals.total === 0) return null;
  const segments = [
    { key: "sold", label: "Sold", color: CHART_COLORS.sold, count: totals.sold || 0 },
    { key: "booked", label: "Booked", color: CHART_COLORS.booked, count: totals.booked || 0 },
    { key: "blocked", label: "Blocked", color: CHART_COLORS.blocked, count: totals.blocked || 0 },
    { key: "available", label: "Available", color: CHART_COLORS.available, count: totals.available || 0 },
  ];
  return (
    <div>
      <div className="flex h-4 w-full overflow-hidden rounded-full">
        {segments.map((seg) => {
          const pct = (seg.count / totals.total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={seg.key}
              className="transition-all"
              style={{ width: `${pct}%`, backgroundColor: seg.color }}
              title={`${seg.label}: ${seg.count}`}
            />
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {segments.map((seg) => (
          <span key={seg.key} className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: seg.color }} />
            {seg.label} {seg.count}
          </span>
        ))}
      </div>
    </div>
  );
}


/* ── Dashboard page ──────────────────────────────────────────────────────── */
export default function Dashboard() {
  const { currentUser, organization, isCaller } = useAuth();
  const [report, setReport] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [loading, setLoading] = useState(true);

  const displayName =
    currentUser?.name?.trim() ||
    currentUser?.email?.split("@")[0] ||
    "there";

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [overview, inventory] = await Promise.allSettled([
          fetchOverview(),
          fetchInventory(),
        ]);
        if (!cancelled) {
          if (overview.status === "fulfilled") setReport(overview.value);
          if (inventory.status === "fulfilled") setInventoryData(inventory.value);
        }
      } catch {
        // Degrade gracefully
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const kpis = report?.kpis;

  const inventoryTotals = inventoryData?.totals
    ? {
        total: inventoryData.totals.total || 0,
        sold: inventoryData.totals.sold || 0,
        booked: inventoryData.totals.booked || 0,
        blocked: inventoryData.totals.blocked || 0,
        available: inventoryData.totals.available || 0,
      }
    : null;

  return (
    <>
      <div className="px-8 py-8">

        {/* ── Page header ───────────────────────────────────────────────── */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              Dashboard
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Welcome back,{" "}
              <span className="font-semibold text-slate-700">{displayName}</span>
              {organization?.name && (
                <>
                  {" "}· {organization.name}
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/reports/overview"
              className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200 transition-colors hover:bg-slate-200"
            >
              <BarChart3 className="h-3.5 w-3.5" />
              View Reports
            </Link>
            <div className="flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-600 ring-1 ring-blue-100">
              <Sparkles className="h-3.5 w-3.5" />
              AI-powered
            </div>
          </div>
        </div>

        {/* ── Row 1: Velocity + Collections + Absorption ────────────────── */}
        <div className="grid grid-cols-3 gap-4">
          {loading ? (
            <>
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-[108px] animate-pulse rounded-xl border border-slate-200 bg-slate-50" />
              ))}
            </>
          ) : (
            <>
              <KpiTile
                label="Avg Days to Win"
                value={kpis?.avg_days_to_win?.current ?? "—"}
                suffix={kpis?.avg_days_to_win?.current != null ? "days" : ""}
                delta={kpis?.avg_days_to_win?.delta != null ? -kpis.avg_days_to_win.delta : null}
                invertDelta
                sublabel="lower is better"
                icon={Timer}
                accentColor="#f59e0b"
              />
              <KpiTile
                label="Collected"
                value={formatINR(kpis?.collected?.current ?? 0)}
                sublabel={kpis?.collected?.attainment_pct != null ? `${kpis.collected.attainment_pct}% of target` : "no target set"}
                icon={Banknote}
                accentColor="#4f39f6"
              />
              <KpiTile
                label="Inventory Absorption"
                value={`${kpis?.absorption_pct?.current ?? 0}%`}
                sublabel={`${kpis?.absorption_pct?.absorbed ?? 0} of ${kpis?.absorption_pct?.total_units ?? 0} units`}
                icon={Building2}
                accentColor="#475569"
              />
            </>
          )}
        </div>

        {/* ── Row 2: My Day + Funnel + Inventory ────────────────────────── */}
        <div className="mt-5 grid grid-cols-3 items-stretch gap-5">
          <div className="col-span-1">
            <MyDayWidget />
          </div>

          <div className="relative col-span-1 overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
            <div className="absolute inset-x-0 top-0 h-[3px] rounded-t-xl" style={{ backgroundColor: "#4f46e5" }} />
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-indigo-600" />
                <span className="text-sm font-semibold text-slate-800">Pipeline Funnel</span>
              </div>
              {!isCaller && (
                <Link to="/reports/funnel" className="flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700">
                  Details <ArrowRight className="h-3 w-3" />
                </Link>
              )}
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
              </div>
            ) : report?.funnel_sparkline?.length > 0 ? (
              <MiniFunnel stages={report.funnel_sparkline} />
            ) : (
              <p className="py-6 text-center text-[13px] text-slate-400">
                No pipeline data yet
              </p>
            )}
          </div>

          <div className="relative col-span-1 overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
            <div className="absolute inset-x-0 top-0 h-[3px] rounded-t-xl" style={{ backgroundColor: "#475569" }} />
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-600" />
                <span className="text-sm font-semibold text-slate-800">Inventory Status</span>
              </div>
              {!isCaller && (
                <Link to="/reports/inventory" className="flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700">
                  Details <ArrowRight className="h-3 w-3" />
                </Link>
              )}
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
              </div>
            ) : inventoryTotals && inventoryTotals.total > 0 ? (
              <InventoryBar totals={inventoryTotals} />
            ) : (
              <p className="py-6 text-center text-[13px] text-slate-400">
                No units in inventory
              </p>
            )}
          </div>
        </div>

        {/* ── Row 3: KPI tiles + Clock ──────────────────────────────────── */}
        <div className="mt-5 flex items-start gap-5">
          <div className="grid min-w-0 flex-1 grid-cols-3 gap-4">
            {loading ? (
              <>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-[108px] animate-pulse rounded-xl border border-slate-200 bg-slate-50" />
                ))}
              </>
            ) : (
              <>
                <KpiTile
                  label="New Leads"
                  value={formatNumber(kpis?.new_leads?.current ?? 0)}
                  delta={kpis?.new_leads?.delta}
                  sublabel={
                    report?.year_over_year
                      ? `vs ${formatNumber(report.year_over_year.new_leads?.last ?? 0)} in ${report.year_over_year.last_year}`
                      : "vs prior period"
                  }
                  icon={Users}
                  accentColor="#3b82f6"
                />
                <KpiTile
                  label="Deals Won"
                  value={formatNumber(kpis?.deals_won?.current ?? 0)}
                  delta={kpis?.deals_won?.delta}
                  sublabel={
                    report?.year_over_year
                      ? `vs ${formatNumber(report.year_over_year.deals_won?.last ?? 0)} in ${report.year_over_year.last_year}`
                      : "vs prior period"
                  }
                  icon={Trophy}
                  accentColor="#10b981"
                />
                <KpiTile
                  label="Win Rate"
                  value={kpis?.win_rate?.current != null ? `${kpis.win_rate.current}%` : "0%"}
                  delta={kpis?.win_rate?.delta}
                  sublabel={
                    report?.year_over_year
                      ? `vs ${report.year_over_year.win_rate?.last ?? 0}% in ${report.year_over_year.last_year}`
                      : "vs prior period"
                  }
                  icon={TrendingUp}
                  accentColor="#8b5cf6"
                />
              </>
            )}
          </div>

          {/* Clock — same size and position */}
          <div className="w-[260px] shrink-0">
            <AttendanceClockWidget />
          </div>
        </div>

        {/* ── Row 4: Document compliance (self-hides when nothing expiring) ── */}
        <div className="mt-5 grid grid-cols-3 gap-5">
          <ExpiringDocumentsWidget />
        </div>

      </div>
    </>
  );
}
